import { FC } from 'react';

import AcceptConflictBold from '@/assets/icons/AcceptConflictBold';
import { useHasPermission } from '@/auth';
import { AccepteerConflictDialog } from '@/components/dialogs/accepteerConflict/AccepteerConflictDialog';
import { Button } from '@/components/UI/Button/Button';
import SidepanelClose from '@/components/UI/Sidepanel/SidepanelClose';
import { Conflict } from '@/types/Conflicten';
import ConflictSidepanel from '@/views/ddp/components/conflict/ConflictSidepanel';
import usePlankTableStore from '@/views/ddp/hooks/usePlankTableStore';
import useSidepanelActions from '@/views/ddp/hooks/useSidepanelActions';
import { geefInzetVoorConflict } from '@/views/ddp/utils/conflict';
import { sidepanelTabTitle } from '@/views/ddp/utils/sidepanel';

import DefectSidepanel from '../../defect/DefectSidepanel';
import ReserveSidepanel from '../../reserve/ReserveSidepanel';

import sidepanelToggleStyles from '../Toggle/SidepanelToggle.module.css';
import styles from './SidepanelHeader.module.css';

export const getConflictInzetId = (conflict?: Conflict): string | null | undefined => {
  if (!conflict) return undefined;
  if (conflict.lastAcceptedPerDay?.length && conflict.elementen.length === 0)
    return conflict.lastAcceptedPerDay[0]?.werklijnElementId;
  if (conflict.type === 'MANCO' && !conflict.lastAcceptedPerDay?.length)
    return geefInzetVoorConflict(conflict)?.werklijnElementId;

  return conflict.elementen[0]?.werklijnElementId;
};

const SidepanelHeader: FC = () => {
  const { closeSidepanel, setTab, setIsAccepteerDialogOpen } = useSidepanelActions();
  const isAllowedToDDPMutatie = useHasPermission('ddp.mutate');
  const sidepanel = usePlankTableStore((state) => state.sidepanel);
  const sidepanelTabs = usePlankTableStore((state) => state.getTabs());
  const defectenByStel = usePlankTableStore((state) => state.defectenByStel);
  const conflictenByStel = usePlankTableStore((state) => state.conflictenByStel);
  const isDialogOpen = sidepanel.isAccepteerDialogOpen;
  const inzetId = getConflictInzetId(sidepanel.conflict);

  return (
    <div className={styles.headerDivStyle}>
      <div className={styles.headerStyle}>
        <h1 className={styles.hStyle}>
          <span className={styles.hCapStyle}>
            {sidepanelTabTitle?.[sidepanel.view as keyof typeof sidepanelTabTitle] ?? sidepanel.view}
          </span>{' '}
          details
        </h1>
        <SidepanelClose close={closeSidepanel} />
      </div>
      <div className={styles.bodyDivStyle}>
        <h5 className={styles.sublabelStyle}>{sidepanel.sublabel}</h5>
        {sidepanel.view === 'defect' && sidepanel.defect && (
          <DefectSidepanel
            defect={sidepanel.defect}
            relatedConflict={conflictenByStel[String(sidepanel.defect.eenheidNummer)]}
          />
        )}
        {sidepanel.view === 'reserve' && sidepanel.reserve && <ReserveSidepanel reserve={sidepanel.reserve} />}
        {sidepanel.view === 'conflict' && sidepanel.conflict && (
          <>
            <ConflictSidepanel
              conflict={sidepanel.conflict}
              relatedDefect={defectenByStel[String(sidepanel.conflict.eenheidNummer)]?.[0]}
            />
            <div style={{ maxWidth: 300, marginTop: 16 }}>
              {isAllowedToDDPMutatie && (
                <Button
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                  onClick={() => setIsAccepteerDialogOpen(true)}
                >
                  <AcceptConflictBold width={20} height={20} fill={'var(--tractie-color-content-interactive-default'} />
                  Accepteer conflict...
                </Button>
              )}
            </div>
            <AccepteerConflictDialog
              showDialog={isDialogOpen}
              hideFetchToast
              handleOpenChange={() => setIsAccepteerDialogOpen(false)}
              onAccepted={closeSidepanel}
              dialogData={[{ werklijnId: sidepanel.conflict.werklijnId, inzetId: inzetId }]}
              title='Accepteer conflicten'
            />
          </>
        )}
      </div>
      <div>
        <div className={sidepanelToggleStyles.tabsMenuDivStyle}>
          {sidepanelTabs?.map((tab) => (
            <div
              key={`sidepanelTabsMenuItem_${tab}`}
              role='button'
              onClick={() => setTab(tab)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') setTab(tab);
              }}
            >
              <h6
                className={`${sidepanelToggleStyles.tabsMenuItemStyle} ${sidepanel.tab === tab ? sidepanelToggleStyles.active : ''}`}
              >
                {tab}
              </h6>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SidepanelHeader;
